import React from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';

import { useSiteSettingsMetadata } from './useSiteSettingsMetadata';

export const MIGRATED_FORMIK_PREFIX = 'settings.siteSettingsV2';

/**
 * Hook to determine the migrated Formik prefix for a site settings section
 * based on the category in the metadata and feature flags.
 * 
 * Migration requires BOTH:
 * 1. vizportalSiteSettingsV2Enabled feature flag = true
 * 2. All settings in the section have category='setting' in metadata
 * 
 * The V2 API format is completely flat - all migrated settings go directly under siteSettingsV2:
 * - 'settings.collectionsEnabled' → 'settings.siteSettingsV2.collectionsEnabled'
 * - 'settings.recycleBinSettings.isRecycleBinEnabled' → 'settings.siteSettingsV2.isRecycleBinEnabled'
 * 
 * @param sectionName - The name of the section (e.g., 'collections', 'recycleBin')
 * @param originalPrefix - The original formik prefix (e.g., 'settings', 'settings.recycleBinSettings')
 * @returns 'settings.siteSettingsV2' if migration conditions are met, otherwise the original prefix
 */
export function useMigratedFormikPrefix(sectionName: string, originalPrefix: string): string {
    const { sectionMetadata } = useSiteSettingsMetadata(sectionName);

    const migratedPrefix = React.useMemo(() => {
        if (sectionMetadata && FeatureFlags.vizportalSiteSettingsV2Enabled) {
            const allSettingsAreMigrated = sectionMetadata.siteSettingMetadata.every(
                setting => setting.category === 'setting'
            );

            if (allSettingsAreMigrated) {
                return MIGRATED_FORMIK_PREFIX;
            }
        }

        return originalPrefix;
    }, [sectionMetadata, originalPrefix]);

    return migratedPrefix;
}

